import {
  createContext,
  useCallback,
  useContext,
  useState,
  ReactNode,
} from 'react';

export const SIDEBAR_TOGGLE_POSITIONS = [
  'top-left',
  'top-right',
  'bottom-left',
  'bottom-right',
] as const;
export type SidebarTogglePosition = (typeof SIDEBAR_TOGGLE_POSITIONS)[number];

const RECENTLY_OPENED_MAX = 15;
export const RECENTLY_OPENED_SIDEBAR_COUNT = 5;

/** One recently opened document; stored by id and path so renames can remove stale entries. */
export interface RecentlyOpenedDocument {
  id: string;
  path: string;
}

export type ColorMode = 'light' | 'dark';

interface AppSettings {
  sidebarTogglePosition: SidebarTogglePosition;
  /** Recently opened documents (id + path), most recent first. Max 15. Legacy: may be string[] (paths only). */
  recentlyOpenedDocuments?: RecentlyOpenedDocument[] | string[];
  /** Document editor: save automatically after idle when enabled. */
  documentAutoSaveEnabled?: boolean;
}

interface AppConfig {
  /** API origin (in dev: same as the webapp origin so Vite can proxy `/api`; production: '' for same-origin). */
  apiHost: string;
  graphqlUrl: string;
  sidebarTogglePosition: SidebarTogglePosition;
  colorMode: ColorMode;
  recentlyOpenedDocuments: RecentlyOpenedDocument[];
  documentAutoSaveEnabled: boolean;
}

export type AppSettingsKey = keyof AppSettings;

/** Fallback when `window` is missing (non-browser); must match Vite dev `server.port`. */
const DEV_WEBAPP_DEFAULT_ORIGIN = 'http://localhost:3001';

/**
 * In dev, API calls use the webapp origin (e.g. `http://eva5.local:3001`) so LAN phones only
 * need reachability to Vite; `vite.config.ts` proxies `/api` to Nest on 127.0.0.1:3000.
 */
function devApiOriginFromPage(): string {
  if (typeof window === 'undefined' || !window.location?.origin) {
    return DEV_WEBAPP_DEFAULT_ORIGIN;
  }
  return window.location.origin;
}

function apiOriginForEnv(): string {
  if (typeof import.meta === 'undefined' || !import.meta.env?.DEV) {
    return '';
  }
  return devApiOriginFromPage();
}

interface AppConfigContextType {
  config: AppConfig;
  setSidebarTogglePosition: (position: SidebarTogglePosition) => void;
  setDocumentAutoSaveEnabled: (enabled: boolean) => void;
  /** Updates theme in UI state only (e.g. after `me` or login). Does not persist. */
  setColorModeFromServer: (mode: ColorMode) => void;
  clearField: (key: AppSettingsKey) => void;
  /** Record that a document was opened; updates recently opened list (max 15). Dedupes by id. */
  recordRecentlyOpenedDocument: (id: string, path: string) => void;
  /** Remove any recent entries for this document id (e.g. after rename so stale path is gone). */
  removeRecentlyOpenedDocumentById: (id: string) => void;
}

const AppConfigContext = createContext<AppConfigContextType | undefined>(
  undefined,
);

interface AppConfigProviderProps {
  children: ReactNode;
}

const defaultSettings: AppSettings = {
  sidebarTogglePosition: 'top-right',
  recentlyOpenedDocuments: [],
  documentAutoSaveEnabled: false,
};

export function AppConfigProvider({ children }: AppConfigProviderProps) {
  const [config, setConfig] = useState<AppConfig>(() => {
    const storedSettings = localStorage.getItem('AppSettings');
    const settings: AppSettings = storedSettings
      ? { ...defaultSettings, ...JSON.parse(storedSettings) }
      : { ...defaultSettings };

    const apiHost = apiOriginForEnv();
    const graphqlUrl = apiHost ? `${apiHost}/api/graphql` : '/api/graphql';

    const raw = settings.recentlyOpenedDocuments;
    const recent: RecentlyOpenedDocument[] =
      raw && Array.isArray(raw)
        ? raw
            .slice(0, RECENTLY_OPENED_MAX)
            .map((item) =>
              typeof item === 'string'
                ? { id: item, path: item }
                : (item as RecentlyOpenedDocument),
            )
        : [];

    return {
      apiHost,
      graphqlUrl,
      sidebarTogglePosition:
        settings.sidebarTogglePosition ?? defaultSettings.sidebarTogglePosition,
      colorMode: 'light',
      recentlyOpenedDocuments: recent,
      documentAutoSaveEnabled: settings.documentAutoSaveEnabled ?? false,
    };
  });

  const saveSettings = (newSettings: Partial<AppSettings>) => {
    const stored = localStorage.getItem('AppSettings');
    const currentSettings: AppSettings = stored
      ? { ...defaultSettings, ...JSON.parse(stored) }
      : { ...defaultSettings };
    const updatedSettings = { ...currentSettings, ...newSettings };
    localStorage.setItem('AppSettings', JSON.stringify(updatedSettings));
  };

  const recordRecentlyOpenedDocument = useCallback(
    (id: string, path: string) => {
      const trimmedPath = path?.trim();
      const trimmedId = id?.trim();
      if (!trimmedPath || !trimmedId) return;
      setConfig((prev) => {
        const list = [...(prev.recentlyOpenedDocuments ?? [])];
        const filtered = list.filter((doc) => doc.id !== trimmedId);
        filtered.unshift({ id: trimmedId, path: trimmedPath });
        const next = filtered.slice(0, RECENTLY_OPENED_MAX);
        const stored = localStorage.getItem('AppSettings');
        const current: AppSettings = stored
          ? { ...defaultSettings, ...JSON.parse(stored) }
          : { ...defaultSettings };
        localStorage.setItem(
          'AppSettings',
          JSON.stringify({ ...current, recentlyOpenedDocuments: next }),
        );
        return { ...prev, recentlyOpenedDocuments: next };
      });
    },
    [],
  );

  const removeRecentlyOpenedDocumentById = useCallback((id: string) => {
    const trimmedId = id?.trim();
    if (!trimmedId) return;
    setConfig((prev) => {
      const list = (prev.recentlyOpenedDocuments ?? []).filter(
        (doc) => doc.id !== trimmedId,
      );
      const stored = localStorage.getItem('AppSettings');
      const current: AppSettings = stored
        ? { ...defaultSettings, ...JSON.parse(stored) }
        : { ...defaultSettings };
      localStorage.setItem(
        'AppSettings',
        JSON.stringify({ ...current, recentlyOpenedDocuments: list }),
      );
      return { ...prev, recentlyOpenedDocuments: list };
    });
  }, []);

  const clearField = (key: AppSettingsKey) => {
    setConfig((prev) => ({
      ...prev,
      [key]: defaultSettings[key],
    }));

    const stored = localStorage.getItem('AppSettings');
    const current: AppSettings = stored
      ? { ...defaultSettings, ...JSON.parse(stored) }
      : { ...defaultSettings };
    const rest = { ...current };
    delete rest[key];
    localStorage.setItem('AppSettings', JSON.stringify(rest));
  };

  const setSidebarTogglePosition = (
    sidebarTogglePosition: SidebarTogglePosition,
  ) => {
    setConfig((prev) => ({ ...prev, sidebarTogglePosition }));
    saveSettings({ sidebarTogglePosition });
  };

  const setDocumentAutoSaveEnabled = (documentAutoSaveEnabled: boolean) => {
    setConfig((prev) => ({ ...prev, documentAutoSaveEnabled }));
    saveSettings({ documentAutoSaveEnabled });
  };

  const setColorModeFromServer = useCallback((colorMode: ColorMode) => {
    setConfig((prev) => ({ ...prev, colorMode }));
  }, []);

  const value: AppConfigContextType = {
    config,
    setSidebarTogglePosition,
    setDocumentAutoSaveEnabled,
    setColorModeFromServer,
    clearField,
    recordRecentlyOpenedDocument,
    removeRecentlyOpenedDocumentById,
  };

  return (
    <AppConfigContext.Provider value={value}>
      {children}
    </AppConfigContext.Provider>
  );
}

export function useAppConfig() {
  const context = useContext(AppConfigContext);
  if (context === undefined) {
    throw new Error('useAppConfig must be used within an AppConfigProvider');
  }
  return context;
}
